import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { router } from './router.js';
import { useAuthStore } from './store/authStore.js';
import { GlobalLoader } from './components/GlobalLoader.js';
import { CookieBanner } from './components/cookies/CookieBanner.js';
import { AccessGate } from './components/AccessGate.js';
import { ThemeProvider } from './providers/ThemeProvider.js';
import { AppErrorBoundary } from './components/RouteErrorPage.js';
import { isReportPrintMode } from './lib/printMode.js';
import { initAnalytics, trackPageView } from './lib/analytics.js';
import { resolveGateState } from './lib/accessGate.js';

export function App() {
  const initializeAuth = useAuthStore((s) => s.initializeAuth);
  const disposeAuthListener = useAuthStore((s) => s.disposeAuthListener);
  const printMode = isReportPrintMode(window.location.pathname, window.location.search);

  // Staging/local tester wall: 'checking' until the API says whether the gate
  // is enabled and (if so) whether this device's token is valid. Production
  // always resolves to 'open'. Print mode (Gotenberg) bypasses the gate —
  // it authenticates with its own short-lived print token.
  const [gate, setGate] = useState<'checking' | 'locked' | 'open'>(printMode ? 'open' : 'checking');

  useEffect(() => {
    initializeAuth();
    return () => disposeAuthListener();
  }, [initializeAuth, disposeAuthListener]);

  useEffect(() => {
    if (gate === 'open') return;
    let cancelled = false;
    void resolveGateState().then((state) => {
      if (!cancelled) setGate(state);
    });
    return () => {
      cancelled = true;
    };
    // Intentionally mount-only: `gate` starts as 'checking' (or 'open' in
    // print mode, where the early return above makes this a no-op).
  }, []);

  useEffect(() => {
    initAnalytics();

    let previousPathname = router.state.location.pathname;

    const unsubscribe = router.subscribe((state) => {
      if (state.location.pathname !== previousPathname) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        trackPageView(state.location.pathname + state.location.search);
      }
      previousPathname = state.location.pathname;
    });

    return unsubscribe;
  }, []);

  if (gate !== 'open') {
    return (
      <AppErrorBoundary>
        <ThemeProvider>
          {gate === 'locked' ? (
            <>
              <Toaster richColors position="top-center" />
              <AccessGate onUnlocked={() => setGate('open')} />
            </>
          ) : (
            /* Brief blank frame while the gate status resolves. */
            <div className="min-h-screen bg-ig-bg-primary" />
          )}
        </ThemeProvider>
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <Toaster richColors position="top-center" />
        <RouterProvider router={router} />
        <GlobalLoader />
        {!printMode && <CookieBanner />}
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
